import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface CountChipProps {
  /** The chip's text: "4 MIS", "5 MRS". */
  label: string;
  /** What the chip is, for a screen reader: "4 MIS slips — show the list". */
  ariaLabel: string;
  /** The card's heading. */
  heading: string;
  /** The card's rows: links the person can follow. */
  children: ReactNode;
  className?: string;
}

const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 160;

/**
 * Owner, 2026-10-02 — a column that would list several references shows ONE count chip instead, and the list opens
 * in a card whose rows are links ("we don't want a cluttered column").
 *
 * The card opens on hover, on keyboard focus + Enter, and on click (touch has no hover). It stays open while the
 * pointer is over the chip or the card, so a link in it can be reached and clicked: that is why it is a Popover and
 * not a Tooltip, whose content is not for interactive elements.
 *
 * It mounts a Radix root, so callers render it only when there are two or more entries; a single entry is a plain
 * link with no root (the Items table's per-row cost, UI-GUIDELINES §8).
 */
export function CountChip({ label, ariaLabel, heading, children, className }: CountChipProps) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A hover-opened card must not pull keyboard focus off whatever the person is doing; a click or Enter may.
  const byHover = useRef(false);

  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const later = (next: boolean, delay: number) => {
    cancel();
    timer.current = setTimeout(() => setOpen(next), delay);
  };
  useEffect(() => cancel, []);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        cancel();
        if (next) byHover.current = false;
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          // The Items row is itself clickable: the chip must not also toggle it.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onPointerEnter={(e) => {
            if (e.pointerType !== 'mouse') return;
            byHover.current = true;
            later(true, OPEN_DELAY_MS);
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === 'mouse') later(false, CLOSE_DELAY_MS);
          }}
          className={cn(
            'inline-flex h-[19px] shrink-0 items-center whitespace-nowrap rounded-md border border-border bg-muted/50 px-[7px] text-[10.5px] font-semibold tabular-nums text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted',
            className,
          )}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        collisionPadding={12}
        onOpenAutoFocus={(e) => {
          if (byHover.current) e.preventDefault();
        }}
        onPointerEnter={cancel}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse' && byHover.current) later(false, CLOSE_DELAY_MS);
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-auto min-w-[220px] max-w-[340px] rounded-[10px] border-border bg-card p-0 text-foreground shadow-lg"
      >
        <div className="border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
          {heading}
        </div>
        <ul className="max-h-[260px] overflow-y-auto py-1">{children}</ul>
      </PopoverContent>
    </Popover>
  );
}

/** One row of a count chip's card. */
export function CountChipRow({ children }: { children: ReactNode }) {
  return <li className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs">{children}</li>;
}
